import React from 'react';
import {
  Box,
  Typography,
  Chip,
  Paper,
} from '@mui/material';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineOppositeContent,
} from '@mui/lab';
import {
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as PendingIcon,
  Error as ErrorIcon,
  Schedule as ScheduleIcon,
  CloudUpload as UploadIcon,
  Assignment as ProcessingIcon,
  Download as CompletedIcon,
} from '@mui/icons-material';
import { JobStatus, type JobResponse } from '@taxassist/api-client';

interface JobStatusTimelineProps {
  statusHistory?: JobResponse['statusUpdateHistory'];
  currentStatus: JobStatus;
  compact?: boolean;
  orientation?: 'vertical' | 'horizontal';
}

interface StatusConfig {
  label: string;
  icon: React.ReactElement;
  color: 'primary' | 'success' | 'error' | 'warning' | 'grey';
  description: string;
}

const STATUS_CONFIG: Record<JobStatus, StatusConfig> = {
  [JobStatus.DRAFT]: {
    label: 'Draft',
    icon: <PendingIcon />,
    color: 'grey',
    description: 'Job has been created and is ready for file uploads',
  },
  [JobStatus.TRIAGE]: {
    label: 'Triage',
    icon: <ScheduleIcon />,
    color: 'primary',
    description: 'Job is awaiting reviewer assignment',
  },
  [JobStatus.SUBMITTED]: {
    label: 'Submitted',
    icon: <ProcessingIcon />,
    color: 'primary',
    description: 'Job has been submitted for processing',
  },
  [JobStatus.PROCESSING]: {
    label: 'Processing',
    icon: <ScheduleIcon />,
    color: 'primary',
    description: 'Tax return is being processed',
  },
  [JobStatus.VERIFICATION]: {
    label: 'Verification',
    icon: <UploadIcon />,
    color: 'warning',
    description: 'Processing completed, awaiting verification',
  },
  [JobStatus.VERIFIED]: {
    label: 'Verified',
    icon: <CheckCircleIcon />,
    color: 'success',
    description: 'Processing verified and ready for delivery',
  },
  [JobStatus.DONE]: {
    label: 'Completed',
    icon: <CompletedIcon />,
    color: 'success',
    description: 'Processing completed successfully, results available',
  },
  [JobStatus.ERROR]: {
    label: 'Error',
    icon: <ErrorIcon />,
    color: 'error',
    description: 'Processing failed, manual review required',
  },
  [JobStatus.CANCELLED]: {
    label: 'Cancelled',
    icon: <ErrorIcon />,
    color: 'error',
    description: 'Job was cancelled and will not be processed',
  },
  [JobStatus.ARCHIVED]: {
    label: 'Archived',
    icon: <PendingIcon />,
    color: 'grey',
    description: 'Job has been archived',
  },
};

const STATUS_ORDER: JobStatus[] = [
  JobStatus.DRAFT,
  JobStatus.TRIAGE,
  JobStatus.SUBMITTED,
  JobStatus.PROCESSING,
  JobStatus.VERIFICATION,
  JobStatus.VERIFIED,
  JobStatus.DONE,
];

const getStatusIndex = (status: JobStatus): number => {
  const index = STATUS_ORDER.indexOf(status);
  return index === -1 ? 0 : index;
};

const calculateDuration = (startTime: string, endTime: string): string => {
  const start = new Date(startTime);
  const end = new Date(endTime);
  const diffMs = end.getTime() - start.getTime();

  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffDays > 0) {
    return `${diffDays} day${diffDays > 1 ? 's' : ''}`;
  } else if (diffHours > 0) {
    return `${diffHours} hour${diffHours > 1 ? 's' : ''}`;
  } else if (diffMinutes > 0) {
    return `${diffMinutes} minute${diffMinutes > 1 ? 's' : ''}`;
  } else {
    return 'Less than a minute';
  }
};

export function JobStatusTimeline({
  statusHistory,
  currentStatus,
  compact = false,
  orientation = 'vertical',
}: JobStatusTimelineProps) {
  // Create timeline items from status history or generate from current status
  const timelineItems = React.useMemo(() => {
    if (statusHistory && statusHistory.length > 0) {
      // Use real status history from API
      return statusHistory
        .sort((a, b) => new Date(a.timestamp || '').getTime() - new Date(b.timestamp || '').getTime())
        .map((item, index) => {
          const config = STATUS_CONFIG[item.status || JobStatus.DRAFT];
          const nextItem = statusHistory[index + 1];
          const duration = item.timestamp && nextItem?.timestamp
            ? calculateDuration(item.timestamp, nextItem.timestamp)
            : undefined;

          return {
            status: item.status || JobStatus.DRAFT,
            timestamp: item.timestamp || new Date().toISOString(),
            notes: item.notes,
            config,
            duration,
            isCurrent: item.status === currentStatus,
            isCompleted: true,
          };
        });
    } else {
      // Generate timeline from current status for jobs without history
      const currentIndex = getStatusIndex(currentStatus);
      return STATUS_ORDER.slice(0, currentIndex + 1).map((status, index) => {
        const config = STATUS_CONFIG[status];
        return {
          status,
          timestamp: new Date().toISOString(), // Placeholder timestamp
          notes: undefined,
          config,
          duration: undefined,
          isCurrent: status === currentStatus,
          isCompleted: index <= currentIndex,
        };
      });
    }
  }, [statusHistory, currentStatus]);

  if (orientation === 'horizontal') {
    // Horizontal timeline for compact display
    return (
      <Box sx={{ py: 2 }}>
        <Typography variant={compact ? 'subtitle2' : 'h6'} sx={{ mb: 2 }}>
          Status Progress
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, overflowX: 'auto', pb: 1 }}>
          {STATUS_ORDER.map((status, index) => {
            const config = STATUS_CONFIG[status];
            const item = timelineItems.find(item => item.status === status);
            const isCompleted = item?.isCompleted || false;
            const isCurrent = status === currentStatus;
            const isActive = isCompleted || isCurrent;

            return (
              <React.Fragment key={status}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 80 }}>
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: isActive ? `${config.color}.main` : 'grey.300',
                      color: isActive ? 'white' : 'grey.500',
                      mb: 1,
                    }}
                  >
                    {React.cloneElement(config.icon, { fontSize: 'small' } as any)}
                  </Box>
                  <Typography
                    variant="caption"
                    sx={{
                      textAlign: 'center',
                      fontWeight: isCurrent ? 600 : 400,
                      color: isActive ? 'text.primary' : 'text.secondary',
                    }}
                  >
                    {config.label}
                  </Typography>
                  {isCurrent && (
                    <Chip
                      label="Current"
                      size="small"
                      color="primary"
                      sx={{ mt: 0.5, fontSize: '0.65rem', height: 16 }}
                    />
                  )}
                </Box>
                {index < STATUS_ORDER.length - 1 && (
                  <Box
                    sx={{
                      flex: 1,
                      height: 2,
                      bgcolor: isCompleted ? `${config.color}.main` : 'grey.300',
                      minWidth: 20,
                    }}
                  />
                )}
              </React.Fragment>
            );
          })}
        </Box>
      </Box>
    );
  }

  // Vertical timeline for detailed display
  return (
    <Box>
      <Timeline position="right">
        {timelineItems.map((item, index) => (
          <TimelineItem key={`${item.status}-${index}`}>
            <TimelineOppositeContent sx={{ flex: 0.3, py: 1 }}>
              <Typography variant="caption" color="text.secondary">
                {new Date(item.timestamp).toLocaleDateString(undefined, {
                  year: '2-digit',
                  month: 'numeric',
                  day: 'numeric',
                })}
              </Typography>
              {item.duration && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  Duration: {item.duration}
                </Typography>
              )}
            </TimelineOppositeContent>

            <TimelineSeparator>
              <TimelineDot
                color={item.config.color}
                variant={item.isCurrent ? 'filled' : 'outlined'}
              >
                {item.config.icon}
              </TimelineDot>
              {index < timelineItems.length - 1 && <TimelineConnector />}
            </TimelineSeparator>

            <TimelineContent sx={{ py: 1 }}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  bgcolor: 'transparent',
                  border: 'none',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Typography variant="subtitle2" fontWeight={600}>
                    {item.config.label}
                  </Typography>
                  {item.isCurrent && (
                    <Chip label="Current" size="small" color="primary" />
                  )}
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {item.config.description}
                </Typography>

                {item.notes && (
                  <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                    Note: {item.notes}
                  </Typography>
                )}
              </Paper>
            </TimelineContent>
          </TimelineItem>
        ))}
      </Timeline>
    </Box>
  );
}
